import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { authService, reportesService } from '../services/api';
import ConfirmPopup from '../components/ConfirmPopup';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './InformeEditor.css';

const BASE_PLANTILLA = (process.env.PUBLIC_URL || '') + '/informe-plantilla';
const TOP_MARGIN_MM = 22;
const BOTTOM_MARGIN_MM = 18;
const CONTENT_AREA_HEIGHT_MM = 297 - TOP_MARGIN_MM - BOTTOM_MARGIN_MM;
const PAGE_NUMBER_Y_MM = 287;

/** Carga una imagen desde la URL y devuelve su data URL para usar en jsPDF, o null si falla. */
function loadImageAsDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Carga imágenes de plantilla según tipo (mensual/anual/blanco). Carátulas y página final sin año/mes: usar versiones ya recortadas. */
async function loadPlantillaImages(template) {
  const isAnual = template === 'anual';
  const [caratulaAnual, caratulaMensual, paginaBlanco, paginaFinalAnual, paginaFinalMensual] = await Promise.all([
    loadImageAsDataUrl(`${BASE_PLANTILLA}/caratula-anual.png`),
    loadImageAsDataUrl(`${BASE_PLANTILLA}/caratula-mensual.png`),
    loadImageAsDataUrl(`${BASE_PLANTILLA}/pagina-blanco.png`),
    loadImageAsDataUrl(`${BASE_PLANTILLA}/pagina-final-anual.png`),
    loadImageAsDataUrl(`${BASE_PLANTILLA}/pagina-final-mensual.png`),
  ]);
  const caratula = isAnual ? caratulaAnual : caratulaMensual;
  const paginaFinal = isAnual ? paginaFinalAnual : paginaFinalMensual;
  return { caratula, paginaBlanco, paginaFinal };
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0'];

const getFuenteColor = (nombre, fallbackIndex = 0) => {
  if (!nombre) return COLORS[fallbackIndex % COLORS.length];
  const n = String(nombre).toLowerCase();
  if (n.includes('whatsapp') || n.includes('wpp') || n.includes('whats')) return '#25D366';
  if (n.includes('instagram') || n.includes('ig')) return '#833AB4';
  if (n.includes('facebook') || n.includes('fb')) return '#1877F2';
  if (n.includes('twitter') || n.includes('x')) return '#000000';
  if (n.includes('google') || n.includes('búsqueda') || n.includes('busqueda')) return '#4285F4';
  if (n.includes('linkedin')) return '#0A66C2';
  if (n.includes('youtube')) return '#FF0000';
  if (n.includes('tiktok')) return '#000000';
  if (n.includes('referido') || n.includes('amigo') || n.includes('conocido')) return '#6B7280';
  return COLORS[fallbackIndex % COLORS.length];
};

const CHART_TYPES = [
  { value: 'genero', label: 'Género' },
  { value: 'situacion_laboral', label: 'Situación laboral' },
  { value: 'nivel_estudio', label: 'Nivel de estudio' },
  { value: 'fuente_informacion', label: 'De dónde nos conocieron' },
  { value: 'inscripciones_iniciados', label: 'Inscriptos vs Iniciados' },
  { value: 'rango_etario', label: 'Rango etario' },
];

function DonutChart({ data, colors, getColor }) {
  const total = data.reduce((s, d) => s + (d.cantidad || 0), 0);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="cantidad"
          nameKey="nombre"
          cx="50%"
          cy="50%"
          outerRadius={90}
          innerRadius={52}
          label={({ nombre, cantidad, midAngle, outerRadius, cx, cy }) => {
            const RADIAN = Math.PI / 180;
            const rOuter = outerRadius + 22;
            const xOut = cx + rOuter * Math.cos(-midAngle * RADIAN);
            const yOut = cy + rOuter * Math.sin(-midAngle * RADIAN);
            const isRight = Math.cos(-midAngle * RADIAN) >= 0;
            const pct = total ? ((cantidad / total) * 100).toFixed(1) : '0';
            return (
              <g>
                <line x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)} y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)} x2={xOut} y2={yOut} stroke="#999" strokeWidth={1} />
                <text x={xOut} y={yOut} fill="#333" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 11, fontWeight: 600 }} dx={isRight ? 4 : -4}>
                  {nombre}
                </text>
                <text x={xOut} y={yOut} fill="#555" textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central" style={{ fontSize: 11 }} dx={isRight ? 4 : -4} dy={12}>
                  {pct}%
                </text>
              </g>
            );
          }}
          labelLine={false}
        >
          {data.map((entry, i) => (
            <Cell key={i} fill={getColor ? getColor(entry.nombre, i) : (colors && colors[i]) || COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => [`${value} (${total ? ((value / total) * 100).toFixed(1) : 0}%)`, '']} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function ChartBlock({ chartType, data }) {
  if (!data) return <div className="chart-wrap">Sin datos</div>;
  const ins = data?.inscripciones || {};
  const porAnio = ins.por_anio || {};
  const year = new Date().getFullYear();
  const totalInsc = (porAnio[String(year)] != null ? porAnio[String(year)] : ins.total) ?? 0;
  const totalIniciados = ins.iniciados ?? 0;
  const barData = [
    { nombre: 'Inscriptos', cantidad: totalInsc },
    { nombre: 'Iniciados', cantidad: totalIniciados },
  ];

  if (chartType === 'inscripciones_iniciados') {
    return (
      <div className="chart-wrap">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={barData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="nombre" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="cantidad" name="Personas" fill={COLORS[0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  let chartData = [];
  let getColor = null;
  switch (chartType) {
    case 'genero':
      chartData = data?.genero || [];
      break;
    case 'situacion_laboral':
      chartData = data?.situacion_laboral || [];
      break;
    case 'nivel_estudio':
      chartData = data?.nivel_estudio || [];
      break;
    case 'fuente_informacion':
      chartData = data?.fuente_informacion || [];
      getColor = getFuenteColor;
      break;
    case 'rango_etario':
      chartData = data?.rango_etario || [];
      break;
    default:
      chartData = [];
  }

  if (chartData.length === 0) return <div className="chart-wrap">Sin datos para este gráfico</div>;

  return (
    <div className="chart-wrap">
      <DonutChart data={chartData} getColor={getColor} />
    </div>
  );
}

function nextId() {
  return `b${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Plantilla basada en informes mensuales (Agosto/Octubre Tecno 3F). */
function getPlantillaMensual() {
  const semestre = new Date().getMonth() < 6 ? '1°' : '2°';
  const anio = new Date().getFullYear();
  return [
    { id: nextId(), type: 'titulo', content: { texto: `Estadísticas Generales (${semestre} Semestre)` } },
    { id: nextId(), type: 'parrafo', content: { texto: `En el presente informe se detallan los resultados de las inscripciones correspondientes al semestre. A continuación se muestran los totales y, luego, las estadísticas por curso.\n\nCompletar con el texto del período (filtros, plazos, cantidad de inscriptos e iniciados, etc.).` } },
    { id: nextId(), type: 'grafico', content: { chartType: 'inscripciones_iniciados' } },
    { id: nextId(), type: 'titulo', content: { texto: 'Estadísticas por curso' } },
    { id: nextId(), type: 'parrafo', content: { texto: 'A modo resumen se confeccionó una tabla con los datos iniciales de cada curso. Luego se analizan en detalle las estadísticas para cada capacitación en particular.\n\nAgregar aquí la tabla o el texto resumen por curso. Podés añadir más bloques (título + párrafo + gráfico) por cada curso.' } },
  ];
}

/** Plantilla basada en Informe Anual Tecno 3F. */
function getPlantillaAnual() {
  const anio = new Date().getFullYear();
  return [
    { id: nextId(), type: 'titulo', content: { texto: 'Introducción' } },
    { id: nextId(), type: 'parrafo', content: { texto: 'Redactar la introducción del informe anual: contexto del programa, crecimiento, metodología y objetivos.\n\nEn los informes de referencia se incluye el origen de Tecno 3F, la evolución de capacitaciones y la mejora en la tasa de egresados.' } },
    { id: nextId(), type: 'titulo', content: { texto: `Estadísticas Finales ${anio}` } },
    { id: nextId(), type: 'parrafo', content: { texto: 'Presentar los resultados globales del año: total de inscriptos, alumnos iniciados y egresados. Incluir comparación con períodos anteriores si corresponde.\n\nAgregar párrafos con los números y su interpretación.' } },
    { id: nextId(), type: 'grafico', content: { chartType: 'inscripciones_iniciados' } },
    { id: nextId(), type: 'titulo', content: { texto: 'Estadísticas por curso' } },
    { id: nextId(), type: 'parrafo', content: { texto: 'Resumen por capacitación. En los informes de referencia se incluye una tabla Curso | Inscriptos | Iniciados y luego una sección por cada curso con título, fecha de inicio, facilitadores, clases y estadísticas.\n\nPodés añadir más bloques (título + párrafo + gráficos) por cada curso.' } },
  ];
}

const PLANTILLAS = [
  { value: 'blanco', label: 'En blanco' },
  { value: 'mensual', label: 'Mensual (semestre)' },
  { value: 'anual', label: 'Anual' },
];

export default function InformeEditor() {
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const [blocks, setBlocks] = useState([]);
  const [template, setTemplate] = useState('blanco');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plantillaPendiente, setPlantillaPendiente] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    reportesService.getDashboard({ year: new Date().getFullYear() })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Error al cargar datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [navigate]);

  const addBlock = (type, content = {}) => {
    setBlocks((prev) => [...prev, { id: nextId(), type, content }]);
    setDropdownOpen(false);
  };

  const updateBlock = (id, content) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, content } : b)));
  };

  const removeBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const moveBlock = (id, dir) => {
    const i = blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };

  const handleImageChange = (id, e) => {
    const file = e.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => updateBlock(id, { dataUrl: reader.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const applyPlantilla = (nuevaPlantilla) => {
    if (nuevaPlantilla === 'blanco') {
      setBlocks([]);
      setTemplate('blanco');
      return;
    }
    if (nuevaPlantilla === 'mensual') {
      setBlocks(getPlantillaMensual());
      setTemplate('mensual');
      return;
    }
    if (nuevaPlantilla === 'anual') {
      setBlocks(getPlantillaAnual());
      setTemplate('anual');
      return;
    }
    setTemplate(nuevaPlantilla);
  };

  const drawPlaceholderCaratula = (pdf, pageWidth, pageHeight) => {
    pdf.setFillColor(245, 247, 250);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    pdf.setTextColor(80, 80, 80);
    pdf.setFontSize(14);
    pdf.text('Carátula (plantilla)', pageWidth / 2 - 30, pageHeight / 2 - 5);
    pdf.text('Reemplazá con: public/informe-plantilla/caratula.png', pageWidth / 2 - 55, pageHeight / 2 + 8);
  };

  const drawPlaceholderPaginaBlanco = (pdf, pageWidth, pageHeight) => {
    pdf.setFillColor(250, 250, 250);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    pdf.setTextColor(120, 120, 120);
    pdf.setFontSize(9);
    pdf.text('Fondo: public/informe-plantilla/pagina-blanco.png', 20, pageHeight / 2 - 3);
  };

  const drawPlaceholderPaginaFinal = (pdf, pageWidth, pageHeight) => {
    pdf.setFillColor(245, 247, 250);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    pdf.setTextColor(80, 80, 80);
    pdf.setFontSize(14);
    pdf.text('Página final (plantilla)', pageWidth / 2 - 35, pageHeight / 2 - 5);
    pdf.text('Reemplazá con: public/informe-plantilla/pagina-final.png', pageWidth / 2 - 58, pageHeight / 2 + 8);
  };

  const drawPageNumber = (pdf, pageNum, totalPages, pageWidth) => {
    const text = String(pageNum);
    const tw = (typeof pdf.getTextWidth === 'function' ? pdf.getTextWidth(text) : (pdf.getTextDimensions?.(text)?.w ?? 5));
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(text, pageWidth - 20 - tw, PAGE_NUMBER_Y_MM);
    pdf.setTextColor(0, 0, 0);
  };

  const handleExportPdf = async () => {
    if (!previewRef.current) return;
    setExportingPdf(true);
    setError('');
    try {
      const contentEl = previewRef.current;
      const { caratula, paginaBlanco, paginaFinal } = await loadPlantillaImages(template);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentAreaHeight = CONTENT_AREA_HEIGHT_MM;

      const canvas = await html2canvas(contentEl, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      const contentHeightPx = contentEl.offsetHeight || contentEl.scrollHeight;
      const titleElements = contentEl.querySelectorAll('.preview-titulo');
      const tituloBlocks = blocks.filter((b) => b.type === 'titulo');
      const indexEntries = [];
      for (let i = 0; i < tituloBlocks.length; i++) {
        const text = tituloBlocks[i].content.texto?.trim() || 'Título';
        let contentPageNum = 1;
        if (contentHeightPx > 0 && titleElements[i]) {
          const posPdf = (titleElements[i].offsetTop / contentHeightPx) * imgHeight;
          contentPageNum = 1 + Math.floor(posPdf / contentAreaHeight);
        }
        indexEntries.push({ text, contentPageNum });
      }

      const totalContentPages = Math.ceil(imgHeight / contentAreaHeight);
      const totalPages = 1 + 1 + totalContentPages + 1;

      if (caratula) {
        pdf.addImage(caratula, 'PNG', 0, 0, pageWidth, pageHeight);
      } else {
        drawPlaceholderCaratula(pdf, pageWidth, pageHeight);
      }

      pdf.addPage();
      if (paginaBlanco) {
        pdf.addImage(paginaBlanco, 'PNG', 0, 0, pageWidth, pageHeight);
      } else {
        drawPlaceholderPaginaBlanco(pdf, pageWidth, pageHeight);
      }
      pdf.setFontSize(18);
      pdf.text('ÍNDICE', 20, TOP_MARGIN_MM + 10);
      pdf.setFontSize(11);
      let yIndice = TOP_MARGIN_MM + 20;
      indexEntries.forEach(({ text, contentPageNum }) => {
        const pdfPage = 2 + contentPageNum;
        const line = text.length > 50 ? text.slice(0, 47) + '...' : text;
        pdf.text(line, 20, yIndice);
        const pageStr = String(pdfPage);
        const tw = (typeof pdf.getTextWidth === 'function' ? pdf.getTextWidth(pageStr) : (pdf.getTextDimensions?.(pageStr)?.w ?? 10));
        pdf.text(pageStr, pageWidth - 20 - tw, yIndice);
        yIndice += 8;
        if (yIndice > pageHeight - BOTTOM_MARGIN_MM - 15) {
          pdf.addPage();
          if (paginaBlanco) pdf.addImage(paginaBlanco, 'PNG', 0, 0, pageWidth, pageHeight);
          else drawPlaceholderPaginaBlanco(pdf, pageWidth, pageHeight);
          yIndice = TOP_MARGIN_MM + 12;
        }
      });
      drawPageNumber(pdf, 2, totalPages, pageWidth);

      const imgData = canvas.toDataURL('image/png');
      let position = 0;
      for (let p = 0; p < totalContentPages; p++) {
        pdf.addPage();
        if (paginaBlanco) {
          pdf.addImage(paginaBlanco, 'PNG', 0, 0, pageWidth, pageHeight);
        } else {
          drawPlaceholderPaginaBlanco(pdf, pageWidth, pageHeight);
        }
        pdf.addImage(imgData, 'PNG', 0, TOP_MARGIN_MM + position, imgWidth, imgHeight);
        const currentPage = 3 + p;
        drawPageNumber(pdf, currentPage, totalPages, pageWidth);
        position -= contentAreaHeight;
      }

      pdf.addPage();
      if (paginaFinal) {
        pdf.addImage(paginaFinal, 'PNG', 0, 0, pageWidth, pageHeight);
      } else {
        drawPlaceholderPaginaFinal(pdf, pageWidth, pageHeight);
      }

      pdf.save(`informe-campus-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error(e);
      setError('Error al generar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="informe-editor-container">
        <h1>Crear informe</h1>
        <div className="informe-editor-loading">Cargando datos...</div>
      </div>
    );
  }

  return (
    <div className="informe-editor-container">
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <ConfirmPopup
        open={Boolean(plantillaPendiente)}
        title="Aplicar plantilla"
        message="Aplicar la plantilla reemplazará los bloques actuales. ¿Continuar?"
        confirmLabel="Aplicar"
        cancelLabel="Cancelar"
        onConfirm={() => {
          if (plantillaPendiente) applyPlantilla(plantillaPendiente);
          setPlantillaPendiente(null);
        }}
        onCancel={() => setPlantillaPendiente(null)}
        variant="primary"
      />
      <div className="informe-editor-header">
        <h1>Crear informe</h1>
        <div className="informe-editor-actions">
          <Link to="/dashboard/estadisticas" className="informe-editor-btn informe-editor-btn-back">
            ← Volver a Estadísticas
          </Link>
          <div className="informe-editor-dropdown">
            <button type="button" className="informe-editor-btn informe-editor-btn-add" onClick={() => setDropdownOpen(!dropdownOpen)}>
              + Añadir bloque
            </button>
            {dropdownOpen && (
              <div className="informe-editor-dropdown-menu">
                <button type="button" onClick={() => addBlock('titulo', { texto: 'Nuevo título' })}>Título</button>
                <button type="button" onClick={() => addBlock('parrafo', { texto: '' })}>Párrafo de texto</button>
                <button type="button" onClick={() => addBlock('grafico', { chartType: 'genero' })}>Gráfico (género, situación laboral, etc.)</button>
                <button type="button" onClick={() => addBlock('imagen', { dataUrl: '' })}>Imagen</button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="informe-editor-btn informe-editor-btn-export"
            onClick={handleExportPdf}
            disabled={exportingPdf || blocks.length === 0}
          >
            {exportingPdf ? 'Generando PDF…' : '📄 Exportar a PDF'}
          </button>
        </div>
      </div>

      <div className="informe-editor-plantilla">
        <label className="informe-editor-plantilla-label">
          Plantilla:
          <select
            value={template}
            onChange={(e) => {
              const v = e.target.value;
              if (blocks.length > 0 && v !== 'blanco') {
                setPlantillaPendiente(v);
              } else {
                applyPlantilla(v);
              }
            }}
            className="informe-editor-plantilla-select"
          >
            {PLANTILLAS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <span className="informe-editor-plantilla-hint">
          {template === 'mensual' && 'Estructura tipo informe mensual/semestral (como Agosto/Octubre).'}
          {template === 'anual' && 'Estructura tipo informe anual (como Informe Anual 2025).'}
          {template === 'blanco' && 'Empezar sin bloques.'}
        </span>
      </div>

      <div className="informe-editor-blocks">
        {blocks.length === 0 && (
          <div className="informe-empty">
            Elegí una plantilla (Mensual o Anual) o usá «Añadir bloque» para armar el informe. El PDF incluirá índice y paginado.
          </div>
        )}
        {blocks.map((block) => (
          <div key={block.id} className="informe-block">
            <div className="informe-block-actions">
              <button type="button" onClick={() => moveBlock(block.id, 'up')} title="Subir">↑</button>
              <button type="button" onClick={() => moveBlock(block.id, 'down')} title="Bajar">↓</button>
              <button type="button" onClick={() => removeBlock(block.id)} title="Eliminar">✕</button>
            </div>
            {block.type === 'titulo' && (
              <div className="informe-block-titulo">
                <input
                  value={block.content.texto || ''}
                  onChange={(e) => updateBlock(block.id, { ...block.content, texto: e.target.value })}
                  placeholder="Título de la sección"
                />
              </div>
            )}
            {block.type === 'parrafo' && (
              <div className="informe-block-parrafo">
                <textarea
                  value={block.content.texto || ''}
                  onChange={(e) => updateBlock(block.id, { ...block.content, texto: e.target.value })}
                  placeholder="Escribí el texto del informe..."
                />
              </div>
            )}
            {block.type === 'grafico' && (
              <div className="informe-block-grafico">
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
                  Tipo de gráfico:
                  <select
                    value={block.content.chartType || 'genero'}
                    onChange={(e) => updateBlock(block.id, { ...block.content, chartType: e.target.value })}
                    style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem' }}
                  >
                    {CHART_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <ChartBlock chartType={block.content.chartType || 'genero'} data={data} />
              </div>
            )}
            {block.type === 'imagen' && (
              <div className="informe-block-imagen">
                {block.content.dataUrl ? (
                  <img src={block.content.dataUrl} alt="Informe" />
                ) : null}
                <input type="file" accept="image/*" onChange={(e) => handleImageChange(block.id, e)} />
              </div>
            )}
          </div>
        ))}
      </div>

      {blocks.length > 0 && (
        <div className="informe-preview-area">
          <h3>Vista previa (así se verá en el PDF)</h3>
          <div ref={previewRef} className="informe-preview-content">
            {blocks.map((block) => (
              <React.Fragment key={block.id}>
                {block.type === 'titulo' && (
                  <h2 className="preview-titulo">{block.content.texto || 'Título'}</h2>
                )}
                {block.type === 'parrafo' && block.content.texto && (
                  <p className="preview-parrafo">{block.content.texto}</p>
                )}
                {block.type === 'grafico' && (
                  <div className="preview-grafico">
                    <ChartBlock chartType={block.content.chartType || 'genero'} data={data} />
                  </div>
                )}
                {block.type === 'imagen' && block.content.dataUrl && (
                  <div className="preview-imagen">
                    <img src={block.content.dataUrl} alt="" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {dropdownOpen && (
        <div
          role="presentation"
          style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
}
